import { atom } from 'jotai';

export interface ShapeMapEntry {
  index: number;
  name: string;
  text: string;
  placeholder_type?: string;
}

export interface SlideData {
  id: string;
  index: number;
  title: string;
  imageUrl?: string;
  notes?: string;
  source?: 'generated' | 'imported';
  extractedText?: string;
  shapeMap?: ShapeMapEntry[];
}

export interface AvailableLayout {
  name: string;
  placeholders: string[];
}

export interface PresentationData {
  id: string;
  title: string;
  slides: SlideData[];
  chatId: string | null;
  pptxUrl?: string;
  previewUrl?: string;
  sourceType?: 'generated' | 'imported';
  availableLayouts?: AvailableLayout[];
}

export interface SlideContext {
  slideIndex: number;
  slideId: string;
  title: string;
  source?: 'generated' | 'imported';
  extractedText?: string;
  shapeMap?: ShapeMapEntry[];
}

export type SlideBuilderMode = 'new' | 'editing';

export const presentationAtom = atom<PresentationData | null>(null);

export const presentationOriginalAtom = atom<PresentationData | null>(null);

export interface PresentationHistoryState {
  past: PresentationData[];
  future: PresentationData[];
}

export const presentationHistoryAtom = atom<PresentationHistoryState>({
  past: [],
  future: [],
});

export const selectedSlideIndexAtom = atom<number>(0);

export const selectedSlideAtom = atom<SlideData | null>((get) => {
  const presentation = get(presentationAtom);
  const index = get(selectedSlideIndexAtom);
  if (!presentation || index < 0 || index >= presentation.slides.length) {
    return null;
  }
  return presentation.slides[index] ?? null;
});

export const slideContextAtom = atom<SlideContext | null>((get) => {
  const slide = get(selectedSlideAtom);
  const index = get(selectedSlideIndexAtom);
  if (!slide) return null;
  return {
    slideIndex: index,
    slideId: slide.id,
    title: slide.title,
    source: slide.source,
    extractedText: slide.extractedText,
    shapeMap: slide.shapeMap,
  };
});

export const slideBuilderModeAtom = atom<SlideBuilderMode>('new');

export const presentationTitleEditingAtom = atom<boolean>(false);

/**
 * Slide context explicitly activated by the user (via the "+" button on the
 * SlideContextChip). When non-null, the next chat message will include this
 * slide as targeted context. Cleared after the message is sent or dismissed.
 */
export const activeSlideContextAtom = atom<SlideContext | null>(null);
